/** The portal notification centre.
 *
 * This was a `CapabilityState` placeholder saying "nothing stores notification,
 * unread, for this contact" — accurate at the time, and no longer: the record
 * exists (`core.Notification`), it is written wherever an outbound email is
 * queued, and this screen lists it.
 *
 * Two things it deliberately does not do. It does not show email delivery
 * state: whether the message bounced or is waiting for quiet hours to end is a
 * different question from whether the reader still needs to look at this, and
 * mixing them would let a mail problem read as an unread notification. And it
 * does not reorder rows when one is marked read — the row would jump under the
 * reader's cursor at the moment they clicked it.
 *
 * The Preferences tab is the end-user half of notification subscriptions. It
 * edits `core.NotificationPreference` through
 * `GET/PATCH /api/portal/notifications/preferences/`, which is scoped to the
 * session — there is no contact or organisation parameter for this screen to
 * send, so it cannot reach anybody else's. A preference only ever *narrows*:
 * the organisation decides which events notify at all, and the panel says so
 * rather than implying somebody can subscribe to more than they are sent.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, api } from '../../lib/api'
import {
  changedPreferences,
  groupByDay,
  groupByKind,
  mutedCount,
  preferenceErrorMessage,
  preferenceKey,
  targetOf,
  type NotificationFeed,
  type PortalNotification,
  type PreferenceFeed,
} from '../../lib/notifications'
import { Card, Spinner, formatDate } from '../../lib/ui'

export function Notifications() {
  const queryClient = useQueryClient()
  const [now] = useState(() => new Date())
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)
  const [tab, setTab] = useState<'feed' | 'preferences'>('feed')

  const feed = useQuery({
    queryKey: ['portal-notifications', showUnreadOnly],
    queryFn: () =>
      api.get<NotificationFeed>(`/notifications/${showUnreadOnly ? '?unread=true' : ''}`),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['portal-notifications'] })

  const setRead = useMutation({
    mutationFn: (payload: { id: number; is_read: boolean }) =>
      api.patchJson(`/notifications/${payload.id}/`, { is_read: payload.is_read }),
    onSuccess: refresh,
  })

  const readAll = useMutation({
    mutationFn: () => api.postJson('/notifications/read-all/', {}),
    onSuccess: refresh,
  })

  const rows = feed.data?.results ?? []
  const unread = feed.data?.unread ?? 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Notifications</h1>
          <p className="mt-1 text-sm text-slate-500">
            {unread > 0
              ? `${unread} unread notification${unread === 1 ? '' : 's'}.`
              : 'Everything here is read.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
            onClick={() => setTab((current) => (current === 'feed' ? 'preferences' : 'feed'))}
          >
            <SlidersHorizontal className="size-3.5" aria-hidden />
            {tab === 'feed' ? 'Preferences' : 'Back to notifications'}
          </button>
          {tab === 'feed' && (
          <button
            type="button"
            className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
            onClick={() => setShowUnreadOnly((only) => !only)}
          >
            {showUnreadOnly ? 'Show all' : 'Unread only'}
          </button>
          )}
          {tab === 'feed' && unread > 0 && (
            <button
              type="button"
              disabled={readAll.isPending}
              className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => readAll.mutate()}
            >
              <CheckCheck className="size-3.5" aria-hidden />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {tab === 'preferences' ? (
        <PreferencesPanel />
      ) : feed.isLoading ? (
        <Spinner />
      ) : feed.isError ? (
        <Card>
          <p className="text-sm text-slate-700">Your notifications could not be loaded.</p>
          <p className="mt-1 text-xs text-slate-500">
            The request failed. Refresh the page; if it keeps happening, the service desk can see
            the error.
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <Bell className="size-6 text-slate-300" aria-hidden />
            <p className="text-sm text-slate-700">
              {showUnreadOnly ? 'Nothing unread.' : 'No notifications yet.'}
            </p>
            <p className="max-w-sm text-xs text-slate-500">
              {showUnreadOnly
                ? 'You are up to date.'
                : 'You will be notified here when the service desk replies to a ticket you raised, when one is resolved, and when an approval needs you.'}
            </p>
          </div>
        </Card>
      ) : (
        groupByDay(rows, now).map((group) => (
          <section key={group.label} className="space-y-2">
            <h2 className="text-xs font-medium uppercase tracking-wide text-slate-400">
              {group.label}
            </h2>
            <Card className="p-0">
              <ul className="divide-y divide-slate-100">
                {group.rows.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onToggleRead={(is_read) => setRead.mutate({ id: notification.id, is_read })}
                  />
                ))}
              </ul>
            </Card>
          </section>
        ))
      )}
    </div>
  )
}

function NotificationRow({
  notification,
  onToggleRead,
}: {
  notification: PortalNotification
  onToggleRead: (is_read: boolean) => void
}) {
  const target = targetOf(notification)

  return (
    <li className={notification.is_read ? 'px-5 py-3' : 'bg-blue-50/40 px-5 py-3'}>
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={
            notification.is_read
              ? 'mt-1.5 size-1.5 shrink-0 rounded-full bg-transparent'
              : 'mt-1.5 size-1.5 shrink-0 rounded-full bg-blue-500'
          }
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-slate-400">{notification.kind_label}</p>
          {target ? (
            <Link
              to={target}
              className="block truncate text-sm font-medium text-slate-900 hover:underline"
              onClick={() => {
                // Opening it is reading it — but only ever in that direction.
                if (!notification.is_read) onToggleRead(true)
              }}
            >
              {notification.title}
            </Link>
          ) : (
            <p className="truncate text-sm font-medium text-slate-900">{notification.title}</p>
          )}
          {notification.body && (
            <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{notification.body}</p>
          )}
          <p className="mt-1 text-xs text-slate-400">
            {formatDate(notification.created_at)}
            {notification.ticket_ref ? ` · ${notification.ticket_ref}` : ''}
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs text-slate-500 hover:text-slate-800"
          onClick={() => onToggleRead(!notification.is_read)}
        >
          {notification.is_read ? 'Mark unread' : 'Mark read'}
        </button>
      </div>
    </li>
  )
}

/** "Tell me about this, on this channel."
 *
 * One row per event, one switch per channel. The two channels are separate on
 * purpose: "stop emailing me about status changes" and "stop telling me at all"
 * are different requests, and a single switch would force somebody who only
 * wanted a quieter inbox to give up the record in the portal as well.
 *
 * Saving sends only what changed, and unmuting sends `null` rather than `true`
 * — see `toggleTo`. Pressing a switch back on therefore returns somebody to
 * whatever their organisation sends, instead of pinning them to today's answer.
 */
function PreferencesPanel() {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Record<string, boolean>>({})

  const preferences = useQuery({
    queryKey: ['portal-notification-preferences'],
    queryFn: () => api.get<PreferenceFeed>('/notifications/preferences/'),
  })

  const rows = preferences.data?.preferences ?? []
  const changes = changedPreferences(draft, rows)

  const save = useMutation({
    mutationFn: () =>
      api.patchJson<PreferenceFeed>('/notifications/preferences/', { preferences: changes }),
    onSuccess: () => {
      setDraft({})
      queryClient.invalidateQueries({ queryKey: ['portal-notification-preferences'] })
    },
  })

  if (preferences.isLoading) return <Spinner />

  if (preferences.isError) {
    const status = preferences.error instanceof ApiError ? preferences.error.status : undefined
    return (
      <Card>
        <p className="text-sm text-slate-700">{preferenceErrorMessage(status)}</p>
      </Card>
    )
  }

  const muted = mutedCount(rows)

  return (
    <div className="space-y-3">
      <Card>
        <p className="text-sm text-slate-700">
          {muted === 0
            ? 'You are receiving everything the service desk sends.'
            : `You have muted ${muted} of ${rows.length} notification${rows.length === 1 ? '' : 's'}.`}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          These settings can only quieten what you already receive. Which events the service desk
          sends at all is decided by your organisation, so switching something on here never adds a
          notification they have turned off.
        </p>
      </Card>

      {save.isError && (
        <Card>
          <p className="text-sm text-slate-700">
            {preferenceErrorMessage(save.error instanceof ApiError ? save.error.status : undefined)}
          </p>
        </Card>
      )}

      <Card className="p-0">
        <ul className="divide-y divide-slate-100">
          {groupByKind(rows).map((row) => (
            <li key={row.kind} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <p className="min-w-0 flex-1 text-sm font-medium text-slate-900">{row.label}</p>
              {(['in_app', 'email'] as const).map((channel) => {
                const preference = row.channels[channel]
                if (!preference) return null
                const key = preferenceKey(row.kind, channel)
                const checked = key in draft ? draft[key] : preference.enabled
                return (
                  <label
                    key={channel}
                    className="flex items-center gap-1.5 text-xs text-slate-600"
                  >
                    <input
                      type="checkbox"
                      className="size-3.5 rounded border-slate-300"
                      checked={checked}
                      onChange={(input) =>
                        setDraft((current) => ({ ...current, [key]: input.target.checked }))
                      }
                    />
                    {preference.channel_label}
                  </label>
                )
              })}
            </li>
          ))}
        </ul>
      </Card>

      {rows.length === 0 && (
        <Card>
          <p className="text-sm text-slate-700">There is nothing to configure yet.</p>
        </Card>
      )}

      <div className="flex items-center justify-end gap-2">
        {changes.length > 0 && (
          <button
            type="button"
            className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
            onClick={() => setDraft({})}
          >
            Discard
          </button>
        )}
        <button
          type="button"
          disabled={changes.length === 0 || save.isPending}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          onClick={() => save.mutate()}
        >
          {changes.length === 0
            ? 'Saved'
            : `Save ${changes.length} change${changes.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  )
}
